import React, { useCallback, useRef, useState, type RefObject } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type {
	ExtensionConfiguration,
	SelectionExtensionComponentProps,
	SelectionExtensionPlugin,
} from '@atlaskit/editor-plugin-selection-extension';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import RandomizeIcon from '@atlaskit/icon-lab/core/randomize';

import { RemixContainer, RemixThinkingBar } from '@atlassian/malleable-ui-components';

const messages = defineMessages({
	remixTooltip: {
		id: 'editor-features.remix.inline-toolbar-tooltip',
		defaultMessage: 'Remix',
		description: 'Tooltip for the Remix button in the editor inline toolbar',
	},
	remixLabel: {
		id: 'editor-features.remix.inline-toolbar-label',
		defaultMessage: 'Remix',
		description: 'Label for the Remix button in the editor inline toolbar',
	},
});

/**
 * Creates a content component that adapts SelectionExtensionComponentProps to RemixContainer.
 * When editorAPIRef is provided, "Insert into page" is wired; otherwise the insert button is hidden.
 */
const createRemixContentComponent = (
	editorAPIRef:
		| RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>
		| undefined,
) => {
	const Component = ({ closeExtension, selection }: SelectionExtensionComponentProps) => {
		const [isThinking, setIsThinking] = useState(false);
		const onOptionSelect = () => {
			setIsThinking(true);
		};

		const handleClose = useCallback(() => {
			closeExtension();
		}, [closeExtension]);

		const handleInsertIntoPage = useCallback((url: string) => {
			const api = editorAPIRef?.current;
			if (!api?.core?.actions?.execute) {
				return;
			}

			try {
				api.core.actions.execute(({ tr }) => {
					const schema = tr.doc.type.schema;
					const { $to } = tr.selection;

					let depth = $to.depth;
					while (depth > 0 && $to.node(depth - 1).type.name !== 'doc') {
						depth--;
					}

					const insertPos = $to.after(depth);
					const embedCardNode = schema.nodeFromJSON({
						type: 'embedCard',
						attrs: {
							url,
							layout: 'center',
							width: 100,
						},
					});

					tr.insert(insertPos, embedCardNode);
					return tr;
				});
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error inserting embedCard:', error);
			}
		}, []);

		// TODO Move RemixThinkingBar into RemixContainer
		return (
			<>
				{isThinking ? <RemixThinkingBar onStop={handleClose} /> : null}
				<RemixContainer
					selectedText={selection.text}
					onClose={handleClose}
					onInsertIntoPage={editorAPIRef ? handleInsertIntoPage : undefined}
					onOptionSelect={onOptionSelect}
				/>
			</>
		);
	};

	return Component;
};

/**
 * Hook that returns an ExtensionConfiguration for the Remix feature
 * using the selection extension's inlineToolbar API.
 *
 * When the toolbar button is clicked, it activates the extension via
 * setActiveExtension, which triggers the SelectionExtensionComponentWrapper
 * to render the content component (RemixContainer: remix overlay then ephemeral preview).
 * When editorAPIRef is provided, "Insert into page" in the ephemeral preview
 * inserts an embedCard smart link into the editor.
 *
 * @param editorAPIRef - Optional ref to the editor's public plugin API. When omitted, the insert button is hidden.
 */
export const useRemixExtension = (
	editorAPIRef?: RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>,
): { extensionList: ExtensionConfiguration[] } => {
	const { formatMessage } = useIntl();

	// Hold a stable reference to the Remix content component so we can pass it to setActiveExtension
	// when the toolbar is clicked; the selection extension API expects the same component reference.
	const contentComponentRef = useRef<React.FC<SelectionExtensionComponentProps> | null>(null);
	if (!contentComponentRef.current) {
		contentComponentRef.current = createRemixContentComponent(editorAPIRef);
	}

	const remixExtension: ExtensionConfiguration = {
		key: 'remix',
		source: 'first-party',
		inlineToolbar: {
			getToolbarItem: () => ({
				icon: RandomizeIcon,
				tooltip: formatMessage(messages.remixTooltip),
				label: formatMessage(messages.remixLabel),
				onClick: () => {
					const api = editorAPIRef?.current;
					if (!api?.selectionExtension || !api?.core) {
						return;
					}

					const selectedText = window.getSelection()?.toString() || '';

					api.core.actions.execute(
						api.selectionExtension.commands.setActiveExtension({
							extension: {
								name: 'Remix',
								component: contentComponentRef.current!,
							},
							selection: {
								text: selectedText,
								from: 0,
								to: 0,
								coords: { top: 0, bottom: 0, left: 0, right: 0 },
							},
						}),
					);
				},
			}),
		},
	};

	return { extensionList: [remixExtension] };
};
